import { z } from "zod";
import type { RunStatus, StepName } from "./runs";
import type { SchemaPolicyMode } from "./schemaChanges";
import type { ImportCause } from "./importCauses";
import {
  CONNECTION_NAME_MAX,
  CONNECTION_NAME_RULE,
  TARGET_NAME_OVERRIDE_RULE,
  TARGET_TABLE_PART_RULE,
  isConnectionName,
  isTargetNameOverride,
  type TableNameCase,
} from "../warehouse/targetTableName";

/**
 * Connection (dataflow) request and response shapes (FR-41 to FR-46, API-STANDARD.md §5).
 *
 * A credential value never appears in any response shape here: a stored credential reads
 * back as `MASKED_VALUE`, and the same sentinel on the way in means "keep what is stored"
 * (FR-43). The route layer is what enforces that; the schemas below only describe the wire.
 */

/**
 * The sentinel a masked credential reads back as, and the one the client sends back
 * unchanged when the user did not retype it. Ported from skypoint-studio's `"*****"`, which
 * the connector form already round-trips, so `ConnectorFormV2` needs no change.
 *
 * It is a sentinel and not an escape: a credential whose literal value is five asterisks
 * cannot be told apart on the wire from "keep what is stored", and is read as the latter.
 * The collision is confined to the wire and to the one case where the two readings agree
 * anyway — a field that already HAS a stored secret, where "keep it" is what the untouched
 * form would have sent. No client decides anything from the sentinel: the editor answers
 * "did the user retype this" from the baseline it seeded the form with, so a value of five
 * asterisks typed into a field with nothing stored is a change like any other and is sent.
 *
 * One gap is left, and it belongs to the route layer rather than to these schemas: a create,
 * or an update to a credential with no `secret_ref` yet, that carries this value has nothing
 * to keep, and should be refused rather than stored as nothing. It is written down here
 * because until it is refused the failure is silent.
 */
export const MASKED_VALUE = "*****";

/** #1356: the sentence for a plain value stored as the placeholder. One text, server and setup page. */
export const MASK_STORED_AS_VALUE_SENTENCE =
  "This value was saved as the hidden placeholder. Enter the value itself.";

/** `<schema>.<table>` for SQL sources, a stream name for API sources. */
export const ENTITY_NAME_MAX = 400;

/**
 * The most entities one connection may carry, discovered or selected (legacy issue A1/A2).
 *
 * The number is a documented ceiling, not a silent truncation: a validation whose source
 * lists more than this is refused with a 422 that names the count, and a selection body
 * longer than this is a 400. It is one constant so discovery and selection can never
 * disagree — the old design let discovery land 7,000 tables and then refused to select more
 * than 2,000 of them, which is the customer that broke Cosmos (Studio #642). 50,000 is
 * well past the largest catalogue any customer has shown (~7,000) and well under what a
 * row-per-entity table and a 10 MB body limit can carry (`server/middleware/bodyLimits.ts`).
 */
export const ENTITY_CATALOGUE_MAX = 50_000;

/**
 * Optimistic concurrency (G3, `STALE_UPDATE`): the `updatedAt` the client loaded. Optional,
 * so an API caller that does not care is never refused; the web client always sends it.
 * A mismatch is a 409 whose `details[0]` is `{ field: "updatedAt", issue: <current ISO> }`.
 */
const expectedUpdatedAtSchema = z.iso.datetime({ offset: true }).optional();

const connectorNameSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/, "A connector name is letters, digits, hyphen or underscore.");

/**
 * The longest connection name the connection editor accepts. The schedule editor's "Add a
 * connection" list shows a name whole up to this length (2026-09-28).
 */
export const DATAFLOW_NAME_MAX = 100;

/** #1099: the Display name's own rules, in the words its field shows (never the key `displayName`). */
export const DISPLAY_NAME_REQUIRED = "A connection needs a name.";
export const DISPLAY_NAME_RULE =
  "A connection name is letters, digits, spaces, hyphen or underscore.";
export const DISPLAY_NAME_TOO_LONG = `A connection name is at most ${DATAFLOW_NAME_MAX} characters.`;
// `*`, not `+`: an empty name is said once, by `min(1)`, not again as a broken rule.
const DISPLAY_NAME_PATTERN = /^[a-zA-Z0-9_\- ]*$/;

/**
 * #1047: the 409 a Display name another connection of the instance holds earns, on create and on
 * rename, and what the editor says at the field before the save when its list already shows one.
 * The name is the one the holder has (#1672: a match ignores case, so not the typed spelling).
 */
export function displayNameTakenMessage(displayName: string): string {
  return `A connection named ${displayName} already exists. Choose another Display name.`;
}

/**
 * #1672: the key two Display names are compared by, in the editor and the server alike
 * (`lower(btrim(display_name))`, runs of spaces folded to one): a name differing only by case or
 * by spaces is the same name to a person, since a page collapses them.
 */
export function displayNameKey(displayName: string): string {
  return displayName.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * The Display name's issue, or null when it is fine: the server's own rules (`dataflowDisplayNameSchema`)
 * and, when `taken` lists the other connections' Display names, the unique rule
 * (compared by `displayNameKey`: case and surrounding spaces do not make a new name).
 */
export function displayNameIssue(
  displayName: string,
  taken: ReadonlySet<string> = new Set(),
): string | null {
  // The schema trims first, then measures: the same order here.
  const value = displayName.trim();
  if (value.length === 0) return DISPLAY_NAME_REQUIRED;
  if (value.length > DATAFLOW_NAME_MAX) return DISPLAY_NAME_TOO_LONG;
  if (!DISPLAY_NAME_PATTERN.test(value)) return DISPLAY_NAME_RULE;
  const key = displayNameKey(value);
  // The sentence names the connection that holds it, as it is listed.
  for (const other of taken) {
    if (displayNameKey(other) === key) return displayNameTakenMessage(other.trim());
  }
  return null;
}

/**
 * The connection's Display name (instance 0015: `dataflows.display_name`; `name` until then).
 * `dataflows_display_name_key` makes it unique per instance, and it is also a Key Vault
 * secret-name part, so the character set stays narrow deliberately.
 */
const dataflowDisplayNameSchema = z
  .string()
  // #1672: stored trimmed, so a trailing space never makes a second "same" name.
  .trim()
  .min(1, DISPLAY_NAME_REQUIRED)
  // PR #1276 review: spaces alone are no name either, so the server and `displayNameIssue` keep
  // one rule. The empty string is `min(1)`'s, said once.
  .refine((value) => value.length === 0 || value.trim() !== "", DISPLAY_NAME_REQUIRED)
  .max(DATAFLOW_NAME_MAX, DISPLAY_NAME_TOO_LONG)
  .regex(DISPLAY_NAME_PATTERN, DISPLAY_NAME_RULE);

/**
 * One submitted form field. The array-of-pairs shape is the one `ConnectorFormV2` posts,
 * and it is the shape the audit redactor already understands: the credential sits under the
 * innocuous key `value`, so a key-based rule alone would miss it (HANDOFF 2026-09-18).
 */
export const dataflowFieldInputSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9_.-]+$/, "A field id is letters, digits, underscore, dot or hyphen."),
  /** `string | number | boolean | null`; the hub is given the value with its type intact. */
  value: z.union([z.string().max(8192), z.number(), z.boolean(), z.null()]).optional(),
});
export type DataflowFieldInput = z.infer<typeof dataflowFieldInputSchema>;

const fieldsArraySchema = z.array(dataflowFieldInputSchema).max(200);

/** One entity the caller wants selected or configured (FR-44). */
export const entitySelectionInputSchema = z.object({
  name: z.string().min(1).max(ENTITY_NAME_MAX),
  isSelected: z.boolean().default(true),
  incrementalEnabled: z.boolean().default(false),
  watermarkColumn: z.string().max(200).nullable().default(null),
  keyColumns: z.array(z.string().max(200)).max(64).default([]),
  /**
   * Round 7 (backlog "A per-table name override", instance 0020): the table's own warehouse name,
   * which wins over `<connection name>_<schema>_<table>`. Absent keeps what is stored (no default: a PATCH
   * keeps it, `putPatchBodies.noDefaults`). Read only since 2026-10-18: a save may echo the stored
   * value and nothing else (`DataflowService.writeTargetNames`).
   */
  targetName: z
    .string()
    .refine(isTargetNameOverride, TARGET_NAME_OVERRIDE_RULE)
    .nullable()
    .optional(),
  /**
   * #2428: a hand-named entity's definition, on a connector whose seed names a `definitionKind`
   * (a GA4 report, `shared/connectors/ga4Report.ts`). Checked by the service against that kind's
   * rule; absent keeps what is stored, and an entry without one never clears a stored one.
   */
  definition: z.unknown().optional(),
});
export type EntitySelectionInput = z.infer<typeof entitySelectionInputSchema>;

/**
 * The keys a create and an update share, WITHOUT defaults. The create adds its defaults to
 * these; the update must not, and that is why it is declared from them rather than derived
 * from the create body: zod 4 keeps a `.default()` under `.partial()` and under `.optional()`,
 * so `createDataflowBodySchema.partial()` still answers `fields: []` for a body that omits
 * `fields`, and `DataflowService.update` reads that as "the user removed every field".
 */
/** The Description's limit, which the editor checks as it is typed (PR #1276 review). */
export const DESCRIPTION_MAX = 1200;
// #1099: in words the panel can show under the field's label, not zod's default.
export const DESCRIPTION_TOO_LONG = `A description is at most ${DESCRIPTION_MAX} characters.`;
const dataflowDescriptionSchema = z.string().max(DESCRIPTION_MAX, DESCRIPTION_TOO_LONG);
const dataflowImageTagSchema = z.string().max(128);
const dataflowRefreshTypeSchema = z.enum(["full", "incremental"]);

/**
 * Round 6 (research N1): the connection's "Name" in the editor (owner 2026-10-01 23:25; the
 * free-text `displayName` is its "Display name"), the first part of every table it loads
 * (`<name>_<schema>_<table>`, `shared/warehouse/targetTableName.ts`). `dataflows.name` since
 * instance 0015 (`table_prefix` before; the API's `tablePrefix`). Optional on create: absent, it
 * is derived from the Display name (`connectionNameFromDisplayName`) and numbered `_2`, `_3` on a
 * clash. Fixed once the connection is saved: an update may repeat it, never change it.
 */
export const connectionNameSchema = z
  .string()
  .max(CONNECTION_NAME_MAX, CONNECTION_NAME_RULE)
  .refine(isConnectionName, CONNECTION_NAME_RULE);

/** The issue a PUT that sends a different `name` is refused with (400). */
export const CONNECTION_NAME_FIXED_ISSUE =
  "The name goes into every table name, so it cannot change after the connection is saved.";

/** Round 7: the issue a change to a landed table's own warehouse name is refused with (400). */
export const TARGET_NAME_FIXED_ISSUE =
  "This table has landed in the warehouse under its name, so the name cannot change.";

/**
 * Owner 2026-10-19: only the table part of a warehouse table name can be chosen. The sentence for
 * each way a typed name is refused (400 on `entities.<table>.targetName`); `prefix` is the fixed
 * start as the destination spells it.
 */
export const targetNameIssues = {
  prefix: (prefix: string) =>
    `The warehouse table name must start with ${prefix}. Only the part after it can be changed.`,
  part: TARGET_TABLE_PART_RULE,
  length: "The warehouse table name is at most 200 characters.",
  reserved: "This name is used by skyData itself. Choose another table part.",
} as const;

/** The 409 a create earns when another connection already holds the typed Name. */
export const CONNECTION_NAME_TAKEN_MESSAGE =
  "Another connection already uses this name in its table names. Choose a different one.";

export const createDataflowBodySchema = z.object({
  connectorName: connectorNameSchema,
  displayName: dataflowDisplayNameSchema,
  name: connectionNameSchema.optional(),
  description: dataflowDescriptionSchema.default(""),
  imageTag: dataflowImageTagSchema.optional(),
  refreshType: dataflowRefreshTypeSchema.default("full"),
  fields: fieldsArraySchema.default([]),
  /** FR-42: saving before anything is selected is allowed, so this may be absent. */
  entities: z.array(entitySelectionInputSchema).max(ENTITY_CATALOGUE_MAX).default([]),
  /**
   * #927: validate on create. The server runs the connection test itself, after the save, with
   * the very fields this body saves, and the answer decides `state` as a re-validation's does. It
   * is a request, never a claim: nothing the client says about an earlier test counts. Needs
   * `dataflows.validate` as well; an AI app cannot send it (it never sends a credential).
   */
  validate: z.boolean().optional(),
});
export type CreateDataflowBody = z.infer<typeof createDataflowBodySchema>;

/**
 * Update: the create body minus `connectorName` (a connection cannot change connector) and
 * `entities` (their own routes), every key optional and NONE defaulted. An omitted key stays
 * absent after the parse, and the service keeps what the row holds for it: a body of only
 * `{ imageTag }` changes only the image tag (defect found live 2026-09-30, when it deleted
 * every stored field). `server/routes/__tests__/putPatchBodies.noDefaults.test.ts` pins it.
 */
export const updateDataflowBodySchema = z.object({
  displayName: dataflowDisplayNameSchema.optional(),
  /** Accepted only when it equals the stored one (`CONNECTION_NAME_FIXED_ISSUE`). */
  name: connectionNameSchema.optional(),
  description: dataflowDescriptionSchema.optional(),
  imageTag: dataflowImageTagSchema.optional(),
  refreshType: dataflowRefreshTypeSchema.optional(),
  /** Present means "these are the fields": a stored field the list omits is deleted. */
  fields: fieldsArraySchema.optional(),
  /**
   * The setup page's "I checked the destination" tick for a connection an AI app drafted
   * (`DataflowDetail.mcpDraftedBy`): a typed credential is refused until it is sent true once.
   */
  confirmDestination: z.boolean().optional(),
  /**
   * #1098: validate on save, as `validate` on create. A Validate of unsaved values records
   * nothing, so the editor asks for this when the last Validate on screen passed on the values it
   * saves; the server runs the test itself on what the save stored, never taking the client's word.
   * Needs `dataflows.validate` as well; an AI app cannot send it.
   */
  validate: z.boolean().optional(),
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type UpdateDataflowBody = z.infer<typeof updateDataflowBodySchema>;

export const validateDataflowBodySchema = z.object({
  connectorName: connectorNameSchema,
  fields: fieldsArraySchema.default([]),
});
export type ValidateDataflowBody = z.infer<typeof validateDataflowBodySchema>;

/** Revalidate (FR-43): any field the user retyped; everything else comes from the row. */
export const revalidateDataflowBodySchema = z.object({
  fields: fieldsArraySchema.default([]),
});
export type RevalidateDataflowBody = z.infer<typeof revalidateDataflowBodySchema>;

/** Full replace (FR-44): a discovered name the body omits is deselected, never deleted. */
export const updateEntitiesBodySchema = z.object({
  entities: z.array(entitySelectionInputSchema).max(ENTITY_CATALOGUE_MAX),
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type UpdateEntitiesBody = z.infer<typeof updateEntitiesBodySchema>;

/**
 * Delta selection (`PATCH /dataflows/:id/entities`), for a client that pages through the
 * catalogue and never holds all of it. Applied in this order, so the result is defined
 * however the client composed it: `selectMatching`, `deselectMatching`, `select`,
 * `deselect`, then `entities`. A name-level op therefore always wins over a search-level
 * one, which is what "select every match, then untick these two" means.
 *
 * `selectMatching`/`deselectMatching` use the same predicate as the entities listing:
 * a case-insensitive substring over the entity name and display name, optionally narrowed
 * to the current view. `entities` upserts the per-entity settings of the rows it names and
 * touches no other row — and it NEVER changes the tick of a discovered row: the ticks are
 * `select`/`deselect`, so a settings entry that still carries the `isSelected` it was read
 * with cannot undo a `deselectMatching` in the same body. `isSelected` in it only counts
 * for a row the PATCH inserts (a hand-named entity on a connector that allows them).
 *
 * A body with none of the five operations is refused: a no-op would still move the
 * connection's stamp and write an audit row.
 */
const entityNamesSchema = z.array(z.string().min(1).max(ENTITY_NAME_MAX)).max(ENTITY_CATALOGUE_MAX);

export const ENTITY_SEARCH_MAX = 200;
export const entitySelectedFilterSchema = z.enum(["all", "selected", "unselected"]);
export type EntitySelectedFilter = z.infer<typeof entitySelectedFilterSchema>;

/**
 * #1465: the Type filter of a connection's table list. A stored NULL `table_type` reads as a table
 * everywhere (`entityTypeOfStored`), so `table` includes it and `view` never does.
 */
export const entityTypeFilterSchema = z.enum(["all", "table", "view"]);
export type EntityTypeFilter = z.infer<typeof entityTypeFilterSchema>;

const matchingSchema = z.object({
  search: z.string().max(ENTITY_SEARCH_MAX).default(""),
  selected: entitySelectedFilterSchema.default("all"),
  entityType: entityTypeFilterSchema.default("all"),
});
export type EntityMatching = z.infer<typeof matchingSchema>;

export const patchEntitiesBodySchema = z
  .object({
    select: entityNamesSchema.optional(),
    deselect: entityNamesSchema.optional(),
    selectMatching: matchingSchema.optional(),
    deselectMatching: matchingSchema.optional(),
    entities: z.array(entitySelectionInputSchema).max(ENTITY_CATALOGUE_MAX).optional(),
    /**
     * #2428: hand-named entities to delete (a GA4 report, an SFTP entity), applied first. Only on
     * a hand-named connector (400 otherwise). The row goes; its bound, table case and table name
     * stay, so the landed table keeps holding its name and a re-add rebuilds it. No warehouse
     * table is ever dropped.
     */
    remove: entityNamesSchema.optional(),
    expectedUpdatedAt: expectedUpdatedAtSchema,
  })
  .refine(
    (body) =>
      Boolean(
        body.select?.length ||
        body.deselect?.length ||
        body.selectMatching ||
        body.deselectMatching ||
        body.entities?.length ||
        body.remove?.length,
      ),
    {
      message:
        "Name at least one operation: select, deselect, selectMatching, deselectMatching, entities or remove.",
    },
  );
export type PatchEntitiesBody = z.infer<typeof patchEntitiesBodySchema>;

export const browseDataflowBodySchema = z.object({
  path: z.string().min(1).max(2048).default("/"),
  fields: fieldsArraySchema.default([]),
});
export type BrowseDataflowBody = z.infer<typeof browseDataflowBodySchema>;

export const dataflowsQuerySchema = z.object({
  state: z.enum(["draft", "ready", "disabled"]).optional(),
  connectorName: connectorNameSchema.optional(),
});
export type DataflowsQuery = z.infer<typeof dataflowsQuerySchema>;

/** The longest reason a Disable or Enable records (the `disabled_reason` column is 200). */
export const DISABLE_REASON_MAX = 200;

/**
 * `POST /dataflows/:id/disable` (2026-09-28, product gaps row 1): the reason is required, 1 to 200
 * characters after trimming. It is stored on the row and on the audit row; it is the admin's own
 * words, so it is shown back on the connection, never logged.
 */
export const disableDataflowBodySchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, "Say why this connection is being disabled.")
    .max(DISABLE_REASON_MAX, `A reason is at most ${DISABLE_REASON_MAX} characters.`),
});
export type DisableDataflowBody = z.infer<typeof disableDataflowBodySchema>;

/** `POST /dataflows/:id/enable`: the reason is optional here; no body at all is accepted. */
export const enableDataflowBodySchema = z
  .object({
    reason: z
      .string()
      .trim()
      .max(DISABLE_REASON_MAX, `A reason is at most ${DISABLE_REASON_MAX} characters.`)
      .optional(),
  })
  .optional()
  .transform((body) => ({ reason: body?.reason ? body.reason : undefined }));
export type EnableDataflowBody = z.infer<typeof enableDataflowBodySchema>;

/** Why and since when a connection is disabled; null while it is enabled. */
export interface DataflowDisabled {
  at: string;
  reason: string;
}

/** The `{status, message, at, by}` block persisted on `dataflows.last_validation`. */
export const lastValidationSchema = z.object({
  status: z.enum(["success", "failed"]),
  message: z.string(),
  at: z.string(),
  /** `users.id` of whoever ran it, never an address (SEC-13). */
  by: z.string().nullable(),
  errorCode: z.string().nullable().optional(),
});
export type LastValidation = z.infer<typeof lastValidationSchema>;

export interface DataflowFieldView {
  id: string;
  /** `MASKED_VALUE` when the field is a credential; the plain value otherwise. */
  value: string | null;
  isCredential: boolean;
  /** Whether a Key Vault reference is stored, so the client can offer "re-validate". */
  hasStoredSecret: boolean;
  /**
   * #1107: true when this reader may not see the value (a source host, user name, client id or
   * email, below `dataflows.write`); `value` is then null. Absent when the value is shown.
   */
  withheld?: boolean;
}

export interface DataflowEntityView {
  name: string;
  displayName: string | null;
  isSelected: boolean;
  incrementalEnabled: boolean;
  watermarkColumn: string | null;
  keyColumns: string[];
  sourceSchema: string | null;
  sourceTable: string | null;
  /** #1465: `table` or `view` (a stored NULL answers `table`). The server always sends it. */
  entityType?: EntityType;
  /**
   * Product gaps row 2: "Data through" for this table, its `entity_watermarks.refreshed_through`
   * (the read start of the last run whose rows for it landed). Absent until a run lands it.
   */
  refreshedThrough?: string;
  /** Product gaps row 2: the run that moved `refreshedThrough` last; absent with it. */
  lastRunId?: string;
  /**
   * Review r2 (owner 2026-10-01 23:10): the Table name case this table's first load named it
   * with (instance 0013), which it keeps whatever the destination's setting says later. Absent
   * until its first load; the setting then applies.
   */
  tableNameCase?: TableNameCase;
  /**
   * Round 7: the table's own warehouse name (instance 0020 `entity_target_name`), which wins over
   * the derived one; absent when it has none.
   */
  targetName?: string;
  /**
   * #2428: a GA4 report's definition (`shared/connectors/ga4Report.ts`), without `kind`. Below
   * `dataflows.write` the property and the filter are withheld (#1107: they name what the
   * connection reaches), read as null with their names in `withheldDefinitionFields`.
   */
  definition?: DataflowEntityDefinitionView;
  /**
   * #2428: true while the report's table has not been rebuilt from its current definition. #2479:
   * false while no table exists yet (never imported): there is nothing to rebuild.
   */
  rebuildPending?: boolean;
}

/** #2428: a report's definition as the entity list answers it. */
export interface DataflowEntityDefinitionView {
  propertyId: string | null;
  dimensions: string[];
  metrics: string[];
  dimensionFilter: Record<string, unknown> | null;
  /** The fields this reader may not see (`propertyId`, `dimensionFilter`); absent when none. */
  withheld?: Array<"propertyId" | "dimensionFilter">;
}

export interface DataflowSummary {
  id: string;
  connectorName: string;
  /** The Display name (instance 0015): free text, unique in the instance, editable. */
  displayName: string;
  /**
   * Round 6 (research N1): the connection's Name (instance 0015: `dataflows.name`, the former
   * `table_prefix`), the first part of every table it loads (`<name>_<schema>_<table>`). Set
   * once at creation, never changed.
   */
  name: string;
  description: string | null;
  imageTag: string | null;
  /**
   * #1077: what an import reads by default. `full` for a connection of a connector that cannot
   * import incrementally, whatever the row stores (`effectiveRefreshType` in DataflowService).
   */
  refreshType: "full" | "incremental";
  state: "draft" | "ready" | "disabled";
  lastValidation: LastValidation | null;
  isProcessing: boolean;
  isFailed: boolean;
  lastRunId: string | null;
  /**
   * The `queued` or `running` run that holds this connection now, else null: one run per
   * connection at a time, so while this is set a Run now is refused (409 `RUN_IN_PROGRESS`) and a
   * firing that needs the connection is skipped. `GET /dataflows` and `GET /dataflows/:id` always
   * send it; it is optional only because other responses built from a summary do not.
   */
  activeRunId?: string | null;
  lastRefreshAt: string | null;
  /**
   * Round 7: whether any of the connection's tables has ever landed (an `entity_watermarks`
   * row), so the first-import note can tell a connection that is new from one that only gained a
   * table (`firstImportNote`). `GET /dataflows` and `GET /dataflows/:id` always send it.
   */
  hasLanded?: boolean;
  /**
   * 2026-09-28: set while the connection is disabled (`state` is `disabled`), else null. It never
   * runs then: Import now is refused (409 `CONNECTION_DISABLED`) and every schedule that names it
   * skips it. `GET /dataflows` and `GET /dataflows/:id` always send it; it is optional only because
   * other responses built from a summary, and clients older than it, do not.
   */
  disabled?: DataflowDisabled | null;
  /**
   * 2026-09-29 (product gaps row 7): what the load does when the source's columns change
   * (`shared/types/schemaChanges.ts`). `GET /dataflows` and `GET /dataflows/:id` always send it.
   */
  schemaPolicy?: SchemaPolicyMode;
  configuredBy: string | null;
  selectedEntityCount: number;
  createdAt: string;
  updatedAt: string;
  /**
   * The run `lastRunId` names, projected for the connections list (S8, owner feedback). Only
   * `GET /dataflows` carries it, and only when `lastRunId` names a run the tenant database
   * still has; it is OMITTED otherwise, so a client that predates it reads the same payload.
   */
  lastRun?: DataflowLastRun;
}

/** #787: why a connection waits, and what happens next (`DataflowLastRun.waiting`). */
export interface DataflowWaiting {
  cause: ImportCause;
  /** The cause's badge words, "Loading not ready". */
  label: string;
  /** What is not in place yet, one sentence without a full stop. */
  sentence: string;
  /** The next step, one line. */
  fix: string;
}

/** A connection's own outcome in a finished run (`DataflowLastRun.outcome`). */
export type ConnectionRunOutcome = Extract<
  RunStatus,
  "succeeded" | "partially_succeeded" | "failed" | "cancelled"
>;

/**
 * One connection's latest run, as its row on the connections list shows it.
 *
 * `status`, `startedAt`, `finishedAt` and `durationSeconds` are the RUN's (what "open the run"
 * shows); a schedule that covers several connections has no per-connection duration, and its
 * per-connection result is `outcome`.
 * `startedAt` is what a list shows for a run still going, whose finish and duration are null.
 * `durationSeconds` is the WHOLE run's, even when it covered other connections: the editor's
 * last-run card hides it for a multi-connection run, so a list column that shows it should say
 * it is the run's, not this connection's. The two
 * row counts are THIS connection's share of the run, under the same rules as `RunTotals`
 * (`shared/types/runs.ts`): `rowsExtracted` is the sum of `entity_progress.records` and
 * `rowsLoaded` the sum of the load's COPY counts. Both are null when the run has no table for
 * this connection yet, and `rowsLoaded` is null until a terminal load reports one. Once the run
 * has a table for this connection, `rowsExtracted` is a number, and it is 0 when only a load
 * reported the table and no `entity_progress` row exists, as in `RunTotals`.
 */
export interface DataflowLastRun {
  runId: string;
  status: RunStatus;
  /** When the run started; null only for a run that has not started yet. */
  startedAt: string | null;
  finishedAt: string | null;
  /** Whole seconds from the run's start to its finish; null until it has both. */
  durationSeconds: number | null;
  rowsExtracted: number | null;
  rowsLoaded: number | null;
  /**
   * THIS connection's own outcome in the run, from its own steps, once the run is over
   * (post-release fix, 2026-09-28): `failed` when one of its steps failed (the rule that sets
   * `is_failed` in `runner/steps/finalize.ts`), else `cancelled` when one was cancelled, else
   * `partially_succeeded` when its extract reported a partial, else `succeeded`. `status` is the
   * whole run's, so a run whose extract succeeded and whose load failed reads
   * partially_succeeded while this reads failed. Omitted while the run is queued or running,
   * when the run has no settled step for this connection, and from an older server.
   *
   * #460 (PR #1450, owner's option 2): when this run was cancelled with no failed step of this
   * connection's own, it settled nothing, and `outcome` is that of the connection's newest
   * settled run before it, named by `outcomeRunId`, so a cancel never hides an earlier failure.
   */
  outcome?: ConnectionRunOutcome;
  /** #460: the run `outcome` describes, present only when it is not `runId` (see `outcome`). */
  outcomeRunId?: string;
  /**
   * #787: present only when `outcome` is `failed` and every failed step of this connection in the
   * run was a readiness gate's refusal (`WAITING_CAUSES`): the connection reads **Waiting**, with
   * this reason and next step, not Failed. The server's words for the stored cause.
   */
  waiting?: DataflowWaiting;
  /**
   * Session 10 (P12): live progress, present only while the run is queued or running and
   * absent once it is over. The steps are this connection's own rows plus the run-level ones
   * (`transform`, `finalize`), so one connection's standard import reads "of 3".
   */
  stepsDone?: number;
  stepsTotal?: number;
  /** The step that is running now; null while queued or between two steps. */
  currentStep?: StepName | null;
  currentStepStartedAt?: string | null;
  /**
   * Tables of the current step: during the extract, the connection's `entity_progress` rows
   * that finished and all of them (null until one is reported); during the load, the
   * `LOAD_ENTITY_*` outcomes of the current attempt over `LOAD_STARTED.payload.entities`.
   * Null in every other step.
   */
  tablesDone?: number | null;
  tablesTotal?: number | null;
  /**
   * #1672: during the load, when its push began sending the tables' files to Snowflake, present
   * only while it sends (a per-tenant load, before any table can finish). Absent for a load with
   * no push, once the push ended, and from an older server.
   */
  sendingFilesSince?: string;
  /**
   * #2389: during the extract, the newest sign that it is moving, from skyData's own records:
   * the extract step's start, its newest progress message (`TABLE_*`, the periodic
   * `TABLE_PROGRESS` among them, or `EXTRACT_*`), whichever is later
   * (`server/services/extractActivity.ts`). Absent in every other step and from an older server.
   */
  lastActivityAt?: string;
  /**
   * #2389: during the extract, the newest activity the connector itself reported (a progress
   * message), absent while it has reported none (the job may still be starting). The page says
   * "Starting the import" until it is set, never "Still reading".
   */
  connectorActivityAt?: string;
}

/**
 * The detail carries the counts and the FIRST PAGE of the catalogue, never all of it: a
 * 7,000-table source would otherwise put megabytes on every open of the editor. The rest
 * is paged and searched through `GET /dataflows/:id/entities` (`shared/types/entities.ts`).
 */
export interface DataflowDetail extends DataflowSummary {
  /**
   * Round 8 review (F3): the AI app that drafted this connection over MCP, else null. While
   * `destinationConfirmedAt` is null, a credential typed for it is refused: the person must first
   * check, on `/connections/:id/setup`, where it will be sent. A change of target clears it.
   * The server always sends both; optional here only so older fixtures need no change.
   */
  mcpDraftedBy?: string | null;
  destinationConfirmedAt?: string | null;
  fields: DataflowFieldView[];
  /**
   * #1107: true when the reader holds no `dataflows.write`, so every field that names where the
   * source is or who it signs in as reads `withheld` (`server/services/sourceIdentity.ts`).
   */
  fieldsWithheld?: boolean;
  /** Every discovered entity, selected or not. */
  entityCount: number;
  /** Same value as `selectedEntityCount`; named beside `entityCount` for the editor. */
  selectedCount: number;
  /** #1465: how many of the entities are views, and how many of those are selected. Absent reads 0. */
  viewCount?: number;
  selectedViewCount?: number;
  /** The first page, sorted by name, `ENTITY_PAGE_SIZE` long. */
  entities: DataflowEntityView[];
  /**
   * #2428: on a connector whose entities carry a definition (GA4), the names of removed reports
   * whose landed table still holds the name (a bound or a first-load case, no entity row), so the
   * Add report drawer can say a re-add rebuilds that table. Absent on every other connector.
   */
  heldReportNames?: string[];
  /**
   * SD2 (#1465): only on an update's answer whose own passing test removed selected tables the
   * source no longer lists (`ValidationView.removedSelected`).
   */
  removedSelected?: { count: number; names: string[] };
  /**
   * #927: only on the answer of a save that asked to validate (a create, or since #1098 an
   * update), and only when that test could not run (the hub or the runner did not answer): the
   * connection is saved as that save left it, and the screen says the check could not run rather
   * than "not validated yet". PR #1350 review: `reason: "not_counted"` (an update only) when the
   * test ran but does not count for what the save stored (a concurrent save overtook it, or it
   * tested other values); no reason when it could not run.
   */
  validationOnCreate?: {
    status: "unavailable";
    /**
     * SD2 (#1465): `VALIDATION_UNAVAILABLE` when the test could not run, else the hub's own
     * no-verdict code (the source answered nothing about this connection, `isNoVerdict`), with
     * `message` the sentence to show.
     */
    code: string;
    reason?: "not_counted";
    message?: string;
  };
}

/** #927: the code of a validate on create that could not run (`DataflowDetail.validationOnCreate`). */
export const VALIDATION_UNAVAILABLE_CODE = "VALIDATION_UNAVAILABLE";

/** The page size the detail's first page and the client's grid use. */
export const ENTITY_PAGE_SIZE = 50;

/** One row of the validator's entity list, already mapped out of the hub's own shape. */
/**
 * #1465 (SD5): what a discovered source object is. The SQL Server catalogue lists views beside tables (the
 * hub's `tableType`, INFORMATION_SCHEMA's `BASE TABLE` or `VIEW`); every other connector lists
 * tables only. Stored as `dataflow_entities.table_type` (instance 0039), where NULL reads as a table.
 */
export const ENTITY_TYPES = ["table", "view"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

/** The hub's `tableType` token as an entity type: `VIEW` is a view, anything else a table. */
export function entityTypeOfHub(tableType: unknown): EntityType {
  return typeof tableType === "string" && tableType.trim().toUpperCase() === "VIEW"
    ? "view"
    : "table";
}

/** A stored `table_type`: NULL, from before instance 0039 or a catalogue with no type, is a table. */
export function entityTypeOfStored(tableType: string | null | undefined): EntityType {
  return tableType === "view" ? "view" : "table";
}

/**
 * #1465: the per-table settings a view never takes. The SQL extractor reads a view in full every
 * run (skyConnect v1.7.0 `VIEW_LOAD_MODE`, `wipe_and_load`), so an incremental mode, a watermark
 * column or a key is refused on save and ignored by the runner if one is stored.
 */
export const VIEW_INCREMENTAL_REFUSED =
  "A view is imported in full each time. Incremental settings do not apply to a view.";

export interface ValidatedEntity {
  name: string;
  displayName: string | null;
  sourceSchema: string | null;
  sourceTable: string | null;
  /** #1465: absent reads as a table (a declared catalogue, or an answer with no `tableType`). */
  entityType?: EntityType;
}

export interface ValidationView {
  status: "success" | "failed";
  message: string;
  errorCode: string | null;
  at: string;
  entities: ValidatedEntity[];
  /**
   * SD2 (#1465): true when the test learned nothing about the connection (a slow or silent source,
   * `isNoVerdict`) and so was not recorded. Only on a re-validate's answer.
   */
  noVerdict?: boolean;
  /**
   * SD2: the selected tables a successful test no longer found in the source, which the refresh
   * removed. `names` is the first ten, sorted. Only on a recorded pass that removed any.
   */
  removedSelected?: { count: number; names: string[] };
}

export interface BrowseItemView {
  name: string;
  path: string | null;
  isDirectory: boolean;
}

export interface BrowseView {
  path: string;
  items: BrowseItemView[];
}
